// Copyright (c) 2026. Hardfin, Inc. All rights reserved.

/**
 * A table component for displaying shipment information with sorting and filtering.
 * This module provides a responsive table view that displays shipment details
 * and supports sorting by various properties.
 */
import { EmptyState } from '@/components/empty-state';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import { ArrowOutwardRounded } from '@/constants/hardfin-standard-icons';
import { SortDirection } from '@/constants/sort';
import { useDisclosure } from '@/hooks/use-disclosure';
import {
  ShipmentFilter,
  ShipmentSortProperty,
} from '@/shipment/api/get-shipments';
import { Shipment, statusToUpperCase } from '@/shipment/api/types';
import { DeliveryDialog } from '@/shipment/components/delivery-dialog';
import { ReceiveDialog } from '@/shipment/components/receive-dialog';
import { ShipmentCancelDialog } from '@/shipment/components/shipment-cancel-dialog';
import { ShipmentTableRow } from '@/shipment/components/shipment-table-row';
import { useShipments } from '@/shipment/hooks/use-shipments';
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { useEffect, useRef } from 'react';

/**
 * Props for the ShipmentTable component.
 */
interface Props {
  /**
   * Optional filter criteria to apply to the shipment list.
   */
  filter?: ShipmentFilter;

  onPageChange: (pageNumber: number) => void;

  /**
   * Optional handler for shipping a shipment (outbound only).
   */
  onShipShipment?: (shipment: Shipment) => void;

  sort?: ShipmentSortProperty;
  direction?: SortDirection;
  onSort?: (property: ShipmentSortProperty) => void;
}

/**
 * A table component for displaying shipment information with sorting and filtering.
 */
export function ShipmentTable(props: Props) {
  const deliverShipmentDialog = useDisclosure();
  const receiveShipmentDialog = useDisclosure();
  const cancelShipmentDialog = useDisclosure();
  const activeShipment = useRef<Shipment>();
  const activeReceipt = useRef<Shipment>();
  const activeCancellation = useRef<Shipment>();

  const handleMarkDelivered = (shipment: Shipment) => {
    activeShipment.current = shipment;
    deliverShipmentDialog.open();
  };
  const handleMarkReceived = (shipment: Shipment) => {
    activeReceipt.current = shipment;
    receiveShipmentDialog.open();
  };
  const handleCancelShipment = (shipment: Shipment) => {
    activeCancellation.current = shipment;
    cancelShipmentDialog.open();
  };
  const handleShipShipment = (shipment: Shipment) => {
    props.onShipShipment?.(shipment);
  };
  const getLabelDirection = (property: ShipmentSortProperty) =>
    props.sort === property && props.direction === 'asc' ? 'asc' : 'desc';
  const getSortDirection = (property: ShipmentSortProperty) =>
    props.sort === property ? getLabelDirection(property) : false;
  const createSortHandler = (property: ShipmentSortProperty) => () =>
    props.onSort?.(property);

  const { shipments, resultCount } = useShipments({
    filter: props.filter,
  });

  // Build download URL with filter parameters for export
  const downloadURL = new URL(
    '/v1/export/shipments',
    import.meta.env.VITE_API_URL
  );
  downloadURL.searchParams.set(
    'shipmentType',
    (props.filter?.shipmentType ?? 'outbound').toUpperCase()
  );
  if (props.filter?.status && props.filter.status !== 'all') {
    downloadURL.searchParams.set(
      'status',
      statusToUpperCase[props.filter.status]
    );
  }
  if (props.filter?.needsAttention) {
    downloadURL.searchParams.set('needsAttention', 'true');
  }

  const pageNumber = props.filter?.pageNumber ?? 1;

  const onPageChange = props.onPageChange;

  // Handle page overrun
  useEffect(() => {
    if (onPageChange && shipments.length === 0 && pageNumber > 1) {
      onPageChange(1);
    }
  }, [shipments.length, pageNumber, onPageChange]);

  if (shipments.length === 0 && props.filter?.needsAttention) {
    return (
      <EmptyState title="No shipments need attention">
        <Typography color="text.secondary">
          Adjust or clear filters to find the shipments you are looking for
        </Typography>
      </EmptyState>
    );
  }

  if (shipments.length === 0) {
    return (
      <EmptyState title="No shipments found">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography color="text.secondary">
            To start tracking shipments in Hardfin, add a new shipment
          </Typography>
          <ArrowOutwardRounded fontSize="small" color="action" />
        </Stack>
      </EmptyState>
    );
  }

  return (
    <>
      <DeliveryDialog
        isOpen={deliverShipmentDialog.isOpen}
        onClose={deliverShipmentDialog.close}
        shipment={activeShipment.current}
      />
      <ReceiveDialog
        isOpen={receiveShipmentDialog.isOpen}
        onClose={receiveShipmentDialog.close}
        shipment={activeReceipt.current}
      />
      {activeCancellation.current && (
        <ShipmentCancelDialog
          isOpen={cancelShipmentDialog.isOpen}
          onClose={cancelShipmentDialog.close}
          shipmentKey={activeCancellation.current.key}
          shipmentNumber={activeCancellation.current.shipmentNumber}
          shippingCost={activeCancellation.current.shippingCost}
        />
      )}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                width="16%"
                sortDirection={getSortDirection('shipmentNumber')}
              >
                <TableSortLabel
                  active={props.sort === 'shipmentNumber'}
                  direction={getLabelDirection('shipmentNumber')}
                  onClick={createSortHandler('shipmentNumber')}
                >
                  References
                </TableSortLabel>
              </TableCell>
              <TableCell width="18%">From/To location</TableCell>
              <TableCell width="27%">Items</TableCell>
              <TableCell
                width="8%"
                align="right"
                sortDirection={getSortDirection('assetValue')}
              >
                <TableSortLabel
                  active={props.sort === 'assetValue'}
                  direction={getLabelDirection('assetValue')}
                  onClick={createSortHandler('assetValue')}
                >
                  Asset value
                </TableSortLabel>
              </TableCell>
              <TableCell
                width="8%"
                align="right"
                sortDirection={getSortDirection('shippingCost')}
              >
                <TableSortLabel
                  active={props.sort === 'shippingCost'}
                  direction={getLabelDirection('shippingCost')}
                  onClick={createSortHandler('shippingCost')}
                >
                  Shipping cost
                </TableSortLabel>
              </TableCell>
              <TableCell
                width="8%"
                align="right"
                sortDirection={getSortDirection('timeInStatus')}
              >
                <TableSortLabel
                  active={props.sort === 'timeInStatus'}
                  direction={getLabelDirection('timeInStatus')}
                  onClick={createSortHandler('timeInStatus')}
                >
                  Time in status
                </TableSortLabel>
              </TableCell>
              <TableCell width="15%" sortDirection={getSortDirection('status')}>
                <TableSortLabel
                  active={props.sort === 'status'}
                  direction={getLabelDirection('status')}
                  onClick={createSortHandler('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shipments.map((shipment) => (
              <ShipmentTableRow
                key={shipment.id}
                shipment={shipment}
                shipmentType={props.filter?.shipmentType}
                handleMarkDelivered={handleMarkDelivered}
                handleMarkReceived={handleMarkReceived}
                handleShipShipment={handleShipShipment}
                handleCancelShipment={handleCancelShipment}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePaginationFooter
        pagination={{
          page: pageNumber,
          pageSize: props.filter?.pageSize ?? 20,
          onPageChange,
        }}
        totalCount={resultCount}
        entityOptions={{ singular: 'shipment', includeCount: false }}
        downloadURL={downloadURL}
        downloadLabel="Export shipment data"
      />
    </>
  );
}
